import {
	type LightningReportScheduleDayOfWeek,
	type LightningReportScheduleFrequency,
	lightningReportScheduleDaysOfWeek,
} from '@xweather/protect-types'
import { type Dayjs, dayjs } from '../../../../dayjs.ts'

// dayjs.day() returns 0=Sun ... 6=Sat. Our `lightningReportScheduleDaysOfWeek` tuple is ['MON', ..., 'SUN'],
// so index 0 → 1 (Mon), index 6 → 0 (Sun).
const dayOfWeekIndex = (day: LightningReportScheduleDayOfWeek) => (lightningReportScheduleDaysOfWeek.indexOf(day) + 1) % 7

// First instance of `dayOfWeek` on or before `from`, at start of day.
const startOfWeeklyPeriod = (from: Dayjs, dayOfWeek: LightningReportScheduleDayOfWeek): Dayjs => {
	const diff = (from.day() - dayOfWeekIndex(dayOfWeek) + 7) % 7
	return from.subtract(diff, 'day')
}

// Returns the start of the period that contains today's calendar date in the given timezone,
// represented as UTC midnight of that calendar date. Schedule dates in the form are stored this
// way so that switching the timezone selector doesn't shift the displayed date.
// - DAILY: today's date at 00:00 UTC
// - WEEKLY: most recent occurrence (on or before today) of the chosen weekday at 00:00 UTC
// - MONTHLY: 1st of the current month at 00:00 UTC
export const currentPeriodStart = (
	frequency: LightningReportScheduleFrequency,
	timezone: string,
	dayOfWeek: LightningReportScheduleDayOfWeek | undefined,
): Dayjs => {
	const todayUtcMidnight = dayjs.utc(dayjs().tz(timezone).format('YYYY-MM-DD'))
	switch (frequency) {
		case 'DAILY':
			return todayUtcMidnight
		case 'WEEKLY':
			return startOfWeeklyPeriod(todayUtcMidnight, dayOfWeek ?? 'MON')
		case 'MONTHLY':
			return todayUtcMidnight.startOf('month')
		default:
			frequency satisfies never
			return todayUtcMidnight
	}
}

export const addPeriod = (date: Dayjs, frequency: LightningReportScheduleFrequency, count = 1): Dayjs => {
	switch (frequency) {
		case 'DAILY':
			return date.add(count, 'day')
		case 'WEEKLY':
			return date.add(count * 7, 'day')
		case 'MONTHLY':
			return date.add(count, 'month')
		default:
			frequency satisfies never
			return date
	}
}

// `count` consecutive period starts beginning at `start`.
export const periodInstances = (start: Dayjs, frequency: LightningReportScheduleFrequency, count: number): Dayjs[] =>
	Array.from({ length: count }, (_, i) => addPeriod(start, frequency, i))
